// ============================================================================
// Agentes de código — registro y presencia (#511): tipos y reglas PURAS.
// ============================================================================
// Puro (sin api/firebase) para que la sección y sus pruebas no arrastren el
// cliente HTTP. Las llamadas viven en serviceDeskService.ts.
// ============================================================================

export type AgentPresence = "online" | "working" | "offline" | "revoked";

export interface ServiceDeskAgent {
  id: string;
  name: string;
  host: string | null;
  version: string | null;
  /** Primeros caracteres de la llave, sólo para distinguir dos agentes. NUNCA la llave. */
  keyPrefix: string | null;
  lastSeenAt: number | null;
  revokedAt: number | null;
  createdAt: number | null;
  createdByName?: string | null;
  currentJob?: { ticketId: string | null; reference?: string | null; since?: unknown } | null;
  presence: AgentPresence;
}

export interface AgentsPanel {
  agents: ServiceDeskAgent[];
  presence: { online: number; working: number; offline: number; revoked: number; total: number; live: number };
  /**
   * `waiting` es un SUELO: el barrido está acotado, así que `capped` indica que
   * hay al menos esos. `unavailable` = el barrido falló y el panel se sirvió
   * igual (la lista de agentes y el botón de revocar no dependen de él).
   */
  queue: {
    enabled: boolean;
    waiting: number;
    oldestWaitingAt: number | null;
    capped?: boolean;
    unavailable?: boolean;
  };
  /**
   * Aviso cuando hay cola de código y CERO agentes conectados. `level: "info"`
   * cuando la mesa sólo usa la llave global heredada (SD_AGENT_KEY, sin
   * presencia): ahí «nadie en línea» no significa «nadie trabajando».
   */
  warning: { code: string; message: string; level?: "info" | "error" } | null;
}

export interface CreatedAgent {
  agent: ServiceDeskAgent;
  /** La llave en claro: la ÚNICA vez que existe fuera del runner. */
  key: string;
  keyHint?: string;
}

export const PRESENCE_COLORS: Record<AgentPresence, { color: string; bg: string }> = {
  online: { color: "#059669", bg: "#d1fae5" },
  working: { color: "#7c3aed", bg: "#ede9fe" },
  offline: { color: "#6b7280", bg: "#f3f4f6" },
  revoked: { color: "#dc2626", bg: "#fee2e2" },
};

export function presenceOf(value: string | null | undefined): AgentPresence {
  return value && value in PRESENCE_COLORS ? (value as AgentPresence) : "offline";
}

/**
 * Texto del contador «Tickets en cola». Un contador que no se pudo calcular es
 * un guion, no un cero que inventa calma; con `capped` se pinta «N+» porque el
 * barrido es acotado — pero el «+» sólo si AÑADE algo («0+» se leería como un
 * número truncado cuando la página volvió llena de tickets no elegibles).
 */
export function queueCountLabel(queue: AgentsPanel["queue"] | null | undefined): string {
  if (!queue || !queue.enabled || queue.unavailable) return "—";
  const waiting = Number.isFinite(queue.waiting) ? queue.waiting : 0;
  return `${waiting}${queue.capped && waiting > 0 ? "+" : ""}`;
}

/** ¿El contador de la cola va en rojo? Hay trabajo esperando y nadie conectado. */
export function queueIsStranded(panel: Pick<AgentsPanel, "queue" | "presence"> | null | undefined): boolean {
  if (!panel?.queue || panel.queue.unavailable || !panel.queue.enabled) return false;
  return panel.queue.waiting > 0 && (panel.presence?.live ?? 0) === 0;
}

/**
 * Mensaje del backend en un error de axios. Importa en el alta y el renombrado:
 * el 403 del aprobador (correo del staff verificado con Google, dominio
 * autorizado) dice exactamente qué le falta a quien lo intenta.
 */
export function apiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown; meta?: { message?: unknown } } } })?.response
    ?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.meta?.message === "string" && data.meta.message) return data.meta.message;
  const msg = (err as { message?: unknown })?.message;
  return typeof msg === "string" ? msg : "";
}
